import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography,
  CircularProgress,
} from "@mui/material";
import {
  BusinessRounded,
  CalendarMonthRounded,
  ReceiptLongRounded,
  SaveRounded,
  StoreRounded,
} from "@mui/icons-material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { useForm, Controller } from "react-hook-form";
import { useSelector } from "react-redux";
import CustomAlert from "../../Common/Components/CustomAlert";

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 2,
  },
};

function Section({ icon, title, description, children }) {
  return (
    <Box
      sx={{
        width: "100%",
        minWidth: 0,
        p: { xs: 1, sm: 2 },
        boxSizing: "border-box",
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "primary.main",
            mt: 0.25,
          }}
        >
          {icon}
        </Box>

        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 700, lineHeight: 1.5 }}
          >
            {title}
          </Typography>

          {description && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.25 }}
            >
              {description}
            </Typography>
          )}
        </Box>
      </Stack>

      <Divider sx={{ mt: 2, mb: 2.5 }} />

      {children}
    </Box>
  );
}

function Company({
  isAdmin,
  company,
  emplacements,
  shareHoldersByEmplacement = [],
  onCompanyUpdate,
}) {
  const { t } = useTranslation(["Company", "Common"]);
  const userState = useSelector((state) => state.user);

  const [discountPayments, setDiscountPayments] = useState([]);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState({
    show: false,
    type: "",
    message: "",
  });

  const { control, reset, handleSubmit, watch } = useForm({
    defaultValues: {
      noAccpac: "",
      nom: "",
      nomEmplacement: "",
      nomExploitant: "",
      dateOuverture: null,
      dateFermeture: null,
      businessName: "",
      noMarchand: "",
      noMarchandDC: "",
      accessCodeMEV: "",
      passwordDC: "",
      gsT_HST: "",
      pst: "",
      idMobiOrderId: "",
      idMobiCatering: "",
      escomptePaiement: "",
    },
  });

  const ouvertureValue = watch("dateOuverture");
  const fermetureValue = watch("dateFermeture");

  const isCompanyClosed = fermetureValue
    ? dayjs(fermetureValue).isBefore(dayjs(), "day")
    : false;

  const isSaveDisabled = saving || (!isAdmin && !company?.estActif);

  useEffect(() => {
    fetchEscomptePaiement();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!company) {
      reset({
        noAccpac: "",
        nom: "",
        nomEmplacement: "",
        nomExploitant: "",
        dateOuverture: null,
        dateFermeture: null,
        businessName: "",
        noMarchand: "",
        noMarchandDC: "",
        accessCodeMEV: "",
        passwordDC: "",
        gsT_HST: "",
        pst: "",
        idMobiOrderId: "",
        idMobiCatering: "",
        escomptePaiement: "",
      });
      return;
    }

    reset({
      noAccpac: company.noAccpac ?? "",
      nom: company.nom ?? "",
      nomEmplacement: company.nomEmplacement ?? "",
      nomExploitant: company.nomExploitant ?? "",

      dateOuverture: company.dateOuverture
        ? dayjs(company.dateOuverture)
        : null,

      dateFermeture: company.dateFermeture
        ? dayjs(company.dateFermeture)
        : null,

      businessName: company.businessName ?? "",

      noMarchand: company.noMarchand == null ? "" : String(company.noMarchand),

      noMarchandDC:
        company.noMarchandDC == null ? "" : String(company.noMarchandDC),

      accessCodeMEV: company.accessCodeMEV ?? "",
      passwordDC: company.passwordDC ?? "",

      gsT_HST: company.gsT_HST ?? company.gst_HST ?? "",

      pst: company.pst ?? company.PST ?? "",

      idMobiOrderId: company.idMobiOrderId ?? "",
      idMobiCatering: company.idMobiCatering ?? "",

      escomptePaiement:
        userState.lang === "EN"
          ? (company.escomptePaiementDetailEn ?? "")
          : (company.escomptePaiementDetailFr ?? ""),
    });
  }, [company, reset, userState.lang]);

  const fetchEscomptePaiement = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/company/GetEscomptePaiement`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            Authorization: `Bearer ${userState.token}`,
          },
        },
      );

      if (!response.ok) {
        throw new Error("Unable to retrieve payment discounts.");
      }

      const data = await response.json();

      setDiscountPayments(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error retrieving payment discounts:", error);
    }
  };

  const numericRules = (messageKey) => ({
    validate: (value) => {
      if (value == null || value === "") return true;

      return /^\d+$/.test(String(value)) || t(messageKey);
    },
  });

  const onSubmit = async (formData) => {
    // Vérification supplémentaire avant l'envoi.
    const validateMobiId = (value) => {
      const normalized = value?.trim() ?? "";

      return normalized === "" || /^[a-zA-Z0-9]{1,10}$/.test(normalized);
    };

    if (
      !validateMobiId(formData.idMobiOrderId) ||
      !validateMobiId(formData.idMobiCatering)
    ) {
      setAlert({
        show: true,
        type: "error",
        message:
          "Les identifiants Mobi doivent contenir uniquement des lettres et des chiffres, avec un maximum de 10 caractères chacun.",
      });
      return;
    }
    setSaving(true);

    const dataCompany = {
      noAccpac: formData.noAccpac,
      nom: formData.nom,
      nomEmplacement: formData.nomEmplacement,
      nomExploitant: formData.nomExploitant,

      dateOuverture: formData.dateOuverture
        ? dayjs(formData.dateOuverture).format("YYYY-MM-DD")
        : null,

      dateFermeture: formData.dateFermeture
        ? dayjs(formData.dateFermeture).format("YYYY-MM-DD")
        : null,

      businessName: formData.businessName,

      noMarchand: formData.noMarchand === "" ? null : formData.noMarchand,

      noMarchandDC: formData.noMarchandDC === "" ? null : formData.noMarchandDC,

      PasswordDC: formData.passwordDC,
      AccessCodeMEV: formData.accessCodeMEV,

      PST: formData.pst,
      GST_HST: formData.gsT_HST,

      escomptePaiement: formData.escomptePaiement,

      // Colonne Emplacement : null lorsque le champ est vide.
      idMobiOrderId: formData.idMobiOrderId?.trim() || null,
      idMobiCatering: formData.idMobiCatering?.trim() || null,
    };

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/company/SaveCompany`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userState.token}`,
          },
          body: JSON.stringify(dataCompany),
        },
      );

      if (!response.ok) {
        const errorMessage = await response.text();

        throw new Error(errorMessage || "Unable to save company.");
      }

      setAlert({
        show: true,
        type: "success",
        message: t("SaveSuccess"),
      });

      if (onCompanyUpdate) {
        await onCompanyUpdate();
      }
    } catch (error) {
      console.error("Error saving company:", error);

      setAlert({
        show: true,
        type: "error",
        message: t("SaveError"),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Box
        sx={{
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          mx: 0,
          p: { xs: 0.5, sm: 1 },
          boxSizing: "border-box",
        }}
      >
        {/* En-tête du formulaire */}
        <Stack spacing={0.5} sx={{ mb: 2 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 800,
              letterSpacing: "-0.3px",
            }}
          >
            {t("Company")}
          </Typography>

          <Typography variant="body2" color="text.secondary">
            {t("CompanyFormDescription")}
          </Typography>
        </Stack>

        {isCompanyClosed && (
          <Box
            sx={{
              mb: 2,
              p: 1.5,
              borderRadius: 2,
              bgcolor: "action.hover",
            }}
          >
            <Typography variant="body2">{t("CompanyClosedMessage")}</Typography>
          </Box>
        )}

        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <Stack spacing={2}>
            {/* 1. Informations générales */}
            <Section
              icon={<BusinessRounded />}
              title={t("GeneralInformation")}
              description={t("GeneralInformationDescription")}
            >
              <Grid container spacing={2.5}>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="noAccpac"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label={t("StoreNumber")}
                        fullWidth
                        size="small"
                        sx={fieldSx}
                        disabled={isCompanyClosed}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Controller
                    name="nom"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label={t("CompanyName")}
                        fullWidth
                        size="small"
                        sx={fieldSx}
                        disabled={isCompanyClosed}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Controller
                    name="nomEmplacement"
                    control={control}
                    render={({ field }) => (
                      <Autocomplete
                        options={emplacements ?? []}
                        getOptionLabel={(option) =>
                          option?.nomEmplacement ?? ""
                        }
                        isOptionEqualToValue={(option, selected) =>
                          option?.noEmplacement === selected?.noEmplacement
                        }
                        value={
                          (emplacements ?? []).find(
                            (item) => item.nomEmplacement === field.value,
                          ) ?? null
                        }
                        onChange={(_, selected) =>
                          field.onChange(selected?.nomEmplacement ?? "")
                        }
                        disabled={isCompanyClosed}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={t("Location")}
                            fullWidth
                            size="small"
                            sx={fieldSx}
                          />
                        )}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Controller
                    name="nomExploitant"
                    control={control}
                    render={({ field }) => (
                      <Autocomplete
                        options={shareHoldersByEmplacement ?? []}
                        getOptionLabel={(option) =>
                          option
                            ? `${option.firstname ?? ""} ${
                                option.lastName ?? ""
                              } (${option.username ?? ""})`.trim()
                            : ""
                        }
                        value={
                          (shareHoldersByEmplacement ?? []).find(
                            (item) =>
                              `${item.firstname ?? ""} ${
                                item.lastName ?? ""
                              } (${item.username ?? ""})`.trim() ===
                              field.value,
                          ) ?? null
                        }
                        onChange={(_, selected) => {
                          field.onChange(
                            selected
                              ? `${selected.firstname ?? ""} ${
                                  selected.lastName ?? ""
                                } (${selected.username ?? ""})`.trim()
                              : "",
                          );
                        }}
                        disabled={isCompanyClosed}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={t("Operator")}
                            fullWidth
                            size="small"
                            sx={fieldSx}
                          />
                        )}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Controller
                    name="businessName"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label={t("BusinessName")}
                        fullWidth
                        size="small"
                        sx={fieldSx}
                        disabled={isCompanyClosed}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Section>

            {/* 2. Dates */}
            <Section
              icon={<CalendarMonthRounded />}
              title={t("CompanyDates")}
              description={t("CompanyDatesDescription", {
                defaultValue: "Dates officielles d'ouverture et de fermeture.",
              })}
            >
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Grid container spacing={2.5}>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="dateOuverture"
                      control={control}
                      render={({ field }) => (
                        <DatePicker
                          label={t("OpenDate")}
                          value={field.value ? dayjs(field.value) : null}
                          onChange={(date) =>
                            field.onChange(
                              date?.isValid()
                                ? date.format("YYYY-MM-DD")
                                : null,
                            )
                          }
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              size: "small",
                              sx: fieldSx,
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="dateFermeture"
                      control={control}
                      rules={{
                        validate: (date) => {
                          if (!date || !ouvertureValue) {
                            return true;
                          }

                          return (
                            !dayjs(date).isBefore(
                              dayjs(ouvertureValue),
                              "day",
                            ) ||
                            t("InvalidCloseDate", {
                              defaultValue:
                                "La date de fermeture doit être égale ou postérieure à la date d'ouverture.",
                            })
                          );
                        },
                      }}
                      render={({ field, fieldState }) => (
                        <DatePicker
                          label={t("CloseDate")}
                          value={field.value ? dayjs(field.value) : null}
                          onChange={(date) =>
                            field.onChange(
                              date?.isValid()
                                ? date.format("YYYY-MM-DD")
                                : null,
                            )
                          }
                          minDate={
                            ouvertureValue ? dayjs(ouvertureValue) : undefined
                          }
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              size: "small",
                              sx: fieldSx,
                              error: !!fieldState.error,
                              helperText: fieldState.error?.message,
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </LocalizationProvider>
            </Section>

            {/* 3. Configuration système */}
            <Section
              icon={<StoreRounded />}
              title={t("SystemConfiguration")}
              description={t("SystemConfigurationDescription")}
            >
              <Grid container spacing={2.5}>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="noMarchand"
                    control={control}
                    rules={numericRules("ValidateNoMarchand")}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        value={field.value ?? ""}
                        onChange={(event) => field.onChange(event.target.value)}
                        label="No Marchand"
                        fullWidth
                        size="small"
                        sx={fieldSx}
                        disabled={isCompanyClosed}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                        inputProps={{ inputMode: "numeric" }}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Controller
                    name="noMarchandDC"
                    control={control}
                    rules={numericRules("ValidateNoMarchandDc")}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        value={field.value ?? ""}
                        onChange={(event) => field.onChange(event.target.value)}
                        label="No Marchand DC"
                        fullWidth
                        size="small"
                        sx={fieldSx}
                        disabled={isCompanyClosed}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                        inputProps={{ inputMode: "numeric" }}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Controller
                    name="accessCodeMEV"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Code d'accès MEV"
                        fullWidth
                        size="small"
                        sx={fieldSx}
                        disabled={isCompanyClosed}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Controller
                    name="passwordDC"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Mot de passe DC"
                        fullWidth
                        size="small"
                        sx={fieldSx}
                        disabled={isCompanyClosed}
                      />
                    )}
                  />
                </Grid>

                {/* Libro ID */}

                {/* Mobi Order ID */}
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="idMobiOrderId"
                    control={control}
                    rules={{
                      validate: (value) => {
                        const normalized = value?.trim() ?? "";

                        return (
                          normalized === "" ||
                          /^[a-zA-Z0-9]{1,10}$/.test(normalized) ||
                          "Mobi Order ID doit contenir uniquement des lettres et des chiffres (10 caractères maximum)."
                        );
                      },
                    }}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        value={field.value ?? ""}
                        onChange={(event) =>
                          field.onChange(event.target.value.slice(0, 10))
                        }
                        label="Mobi Order ID"
                        placeholder="Ex. MOBI123"
                        fullWidth
                        size="small"
                        sx={fieldSx}
                        disabled={isCompanyClosed}
                        error={!!fieldState.error}
                        helperText={
                          fieldState.error?.message ?? t("FacultatifCaracter")
                        }
                        inputProps={{
                          maxLength: 10,
                          autoComplete: "off",
                        }}
                      />
                    )}
                  />
                </Grid>

                {/* Mobi Catering ID */}
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="idMobiCatering"
                    control={control}
                    rules={{
                      validate: (value) => {
                        const normalized = value?.trim() ?? "";

                        return (
                          normalized === "" ||
                          /^[a-zA-Z0-9]{1,10}$/.test(normalized) ||
                          "Mobi Catering ID doit contenir uniquement des lettres et des chiffres (10 caractères maximum)."
                        );
                      },
                    }}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        value={field.value ?? ""}
                        onChange={(event) =>
                          field.onChange(event.target.value.slice(0, 10))
                        }
                        label="Mobi Catering ID"
                        placeholder="Ex. CATERING12"
                        fullWidth
                        size="small"
                        sx={fieldSx}
                        disabled={isCompanyClosed}
                        error={!!fieldState.error}
                        helperText={
                          fieldState.error?.message ?? t("FacultatifCaracter")
                        }
                        inputProps={{
                          maxLength: 10,
                          autoComplete: "off",
                        }}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Section>

            {/* 4. Taxes et paiements */}
            <Section
              icon={<ReceiptLongRounded />}
              title={t("TaxAndPayments")}
              description={t("TaxAndPaymentsDescription")}
            >
              <Grid container spacing={2.5}>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="gsT_HST"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="GST/HST"
                        fullWidth
                        size="small"
                        sx={fieldSx}
                        disabled={isCompanyClosed}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Controller
                    name="pst"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="PST"
                        fullWidth
                        size="small"
                        sx={fieldSx}
                        disabled={isCompanyClosed}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Controller
                    name="escomptePaiement"
                    control={control}
                    render={({ field }) => (
                      <Autocomplete
                        options={discountPayments ?? []}
                        getOptionLabel={(option) =>
                          userState.lang === "FR"
                            ? (option?.nomFr ?? "")
                            : (option?.nomEn ?? "")
                        }
                        isOptionEqualToValue={(option, selected) =>
                          option?.nomFr === selected?.nomFr &&
                          option?.nomEn === selected?.nomEn
                        }
                        value={
                          (discountPayments ?? []).find(
                            (item) =>
                              item.nomFr === field.value ||
                              item.nomEn === field.value,
                          ) ?? null
                        }
                        onChange={(_, selected) =>
                          field.onChange(
                            selected
                              ? userState.lang === "FR"
                                ? selected.nomFr
                                : selected.nomEn
                              : "",
                          )
                        }
                        disabled={isCompanyClosed}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={t("PaymentDiscount")}
                            fullWidth
                            size="small"
                            sx={fieldSx}
                          />
                        )}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Section>

            {/* Bouton Enregistrer */}
            <Divider />

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              justifyContent="space-between"
              alignItems={{ xs: "stretch", sm: "center" }}
              sx={{
                width: "100%",
                minWidth: 0,
                boxSizing: "border-box",
                pt: 1,
                pb: 1,
              }}
            >
              <Typography variant="caption" color="text.secondary">
                {t("RequiredFieldsNote")}
              </Typography>

              <Button
                type="submit"
                variant="contained"
                size="large"
                disableElevation
                disabled={isSaveDisabled}
                startIcon={
                  saving ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <SaveRounded />
                  )
                }
                sx={{
                  minWidth: { xs: "100%", sm: 180 },
                  maxWidth: "100%",
                  borderRadius: 2,
                  px: 3,
                  py: 1.2,
                  fontWeight: 700,
                  textTransform: "none",
                  boxSizing: "border-box",
                }}
              >
                {saving
                  ? t("Saving", {
                      defaultValue: "Enregistrement...",
                    })
                  : t("Save")}
              </Button>
            </Stack>
          </Stack>
        </form>
      </Box>

      <CustomAlert
        open={alert.show}
        closeMessage={() =>
          setAlert((previous) => ({
            ...previous,
            show: false,
          }))
        }
        typeMessage={alert.type}
        message={alert.message}
      />
    </>
  );
}

export default Company;
